import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  Card,
  Divider
} from '@mui/material';
import { BiRefresh } from 'react-icons/bi';
import { useDispatch } from 'react-redux';
import { useGetTrainingRoutineQuery, userTrainingApi } from 'redux/reducers/user/dashboard';
import { Routine } from 'redux/reducers/user/types/GetRoutine';
import TabPanel from './TabPanel';

const SessionTabs = () => {
  const [value, setValue] = useState<number>(0);
  const [routineId, setRoutineId] = useState<number>();
  const dispatch = useDispatch();

  const {
    data: trainingData = [],
    isLoading: isTrainingDataLoading,
    isFetching: isTrainingDataFetching,
    isError,
    refetch
  } = useGetTrainingRoutineQuery('active');

  useEffect(() => {
    if (trainingData.length > 0) {
      setRoutineId(trainingData[value]?.id || trainingData[0].id);
    }
  }, [trainingData, value]);

  const handleChange = (e: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    if (trainingData[newValue]) {
      setRoutineId(trainingData[newValue].id);
    }
  };

  // reloads the tab list and the session/feedback content rendered inside TabPanel
  const handleReload = () => {
    refetch();
    dispatch(userTrainingApi.util.invalidateTags(['Routine', 'DailySession']));
  };

  const reloadButton = (
    <Tooltip title="Reload sessions">
      <span>
        <IconButton size="small" onClick={handleReload} disabled={isTrainingDataFetching}>
          {isTrainingDataFetching ? <CircularProgress size={16} /> : <BiRefresh size={20} />}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box sx={{ width: '100%' }}> {/* Removed Container, replaced with Box */}
      {isTrainingDataLoading ? (
        <Box sx={{ width: '100%' }}>
          <Skeleton variant="rectangular" height={50} sx={{ borderRadius: 2, mb: 2 }} />
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
        </Box>
      ) : isError ? (
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            p: 4,
            textAlign: 'center'
          }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Unable to load sessions.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={handleReload}
            disabled={isTrainingDataFetching}
            startIcon={
              isTrainingDataFetching ? <CircularProgress size={14} /> : <BiRefresh size={18} />
            }>
            Reload
          </Button>
        </Card>
      ) : (
        <Card elevation={0} sx={{
          borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden',
          width: '100%' // Ensure card fills the grid

        }}>
          <Box
            sx={{
              px: 2,
              pt: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>Sessions</Typography>
            {reloadButton}
          </Box>
          <Box sx={{ px: 2, bgcolor: 'background.paper' }}>
            <Tabs
              value={value}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile

              sx={{
                width: '100%',
                maxWidth: '100%',
                // Ensures smooth native scrolling on touch devices
                '& .MuiTabs-scroller': {
                  overflowX: 'auto !important',
                  scrollbarWidth: 'none', // Hide scrollbar for Firefox
                  '&::-webkit-scrollbar': {
                    display: 'none', // Hide scrollbar for Chrome/Safari
                  },
                },
                // Makes scroll buttons easy to click on small screens
                '& .MuiTabs-scrollButtons': {
                  width: 32,
                  '&.Mui-disabled': {
                    opacity: 0.3,
                  },
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  minHeight: 48,
                  minWidth: 'auto', // Allows tabs to fit better
                  px: { xs: 1.5, sm: 2 }, // Reduced horizontal padding on small screens
                  whiteSpace: 'nowrap',
                },
              }}
            >
              {trainingData?.map((item: Routine, index: number) => (
                <Tab
                  key={item.id}
                  label={item.training.training_name}
                  id={`tab-${index}`}
                />
              ))}
            </Tabs>
          </Box>
          <Divider />
          <Box
            sx={{
              minHeight: 500,
              bgcolor: '#f8f9fa',
              opacity: isTrainingDataFetching ? 0.6 : 1,
              transition: 'opacity 0.2s ease'
            }}>
            {routineId && <TabPanel routineId={routineId} value={value} />}
          </Box>
        </Card>
      )}
    </Box>
  );
};

export default SessionTabs;